import cn from 'classnames';
import {useMemo} from 'react';
import {DayFlag, DayPicker, type MonthCaptionProps, SelectionState, UI} from 'react-day-picker';

import CalendarHeader from './CalendarHeader';
import type {CalendarProps} from './types';

const DAY_PICKER_CLASS_NAMES = {
    [UI.Months]: 'flex flex-col',
    [UI.Month]: 'space-y-4',
    [UI.MonthCaption]: 'relative flex items-center justify-center px-2',
    [UI.CaptionLabel]: 'text-16 font-bold leading-24 text-primary-dark',
    [UI.NextMonthButton]: 'hidden',
    [UI.PreviousMonthButton]: 'hidden',
    [UI.MonthGrid]: 'w-full border-separate border-spacing-y-1 border-spacing-x-1',
    [UI.Weekday]: 'h-[46px] text-center text-14 font-bold leading-20',
    [UI.Day]: 'h-[46px] relative p-0 text-center',
    [UI.DayButton]: cn(
        'text-14 leading-20 text-primary-dark font-normal inline-flex size-[46px] items-center justify-center text-center cursor-pointer',
        'md:w-full',
        'disabled:cursor-default disabled:rounded-[2px] disabled:bg-primary-dark-5 disabled:text-primary-dark-85'
    ),
    [SelectionState.selected]: '[&>button]:rounded [&>button]:bg-tertiary [&>button]:font-bold [&>button]:text-primary',
    [DayFlag.today]: '',
};

const Calendar = ({
    value,
    onChange,
    weekdayLabels,
    weekStartsOn,
    formatHeaderLabel,
    disabled,
    startMonth,
    endMonth,
    ariaLabel,
    ariaLabelPrevMonth = '',
    ariaLabelNextMonth = '',
    className,
    mode = 'single',
}: CalendarProps) => {
    const defaultMonth = useMemo(() => value ?? startMonth ?? new Date(), [startMonth, value]);
    const monthCaptionComponent = useMemo(
        () => (props: MonthCaptionProps) => (
            <CalendarHeader
                {...props}
                formatHeaderLabel={formatHeaderLabel}
                ariaLabelPrevMonth={ariaLabelPrevMonth}
                ariaLabelNextMonth={ariaLabelNextMonth}
            />
        ),
        [ariaLabelNextMonth, ariaLabelPrevMonth, formatHeaderLabel]
    );
    const formatters = useMemo(
        () => ({
            formatWeekdayName: (weekday: Date) => {
                const dayIndex = weekday.getDay();

                return weekdayLabels[dayIndex];
            },
        }),
        [weekdayLabels]
    );
    const labels = useMemo(
        () => ({
            labelPrevious: () => ariaLabelPrevMonth,
            labelNext: () => ariaLabelNextMonth,
        }),
        [ariaLabelNextMonth, ariaLabelPrevMonth]
    );

    return (
        <div className={className} aria-label={ariaLabel}>
            <DayPicker
                mode={mode}
                selected={value}
                onSelect={onChange}
                weekStartsOn={weekStartsOn}
                disabled={disabled}
                startMonth={startMonth}
                endMonth={endMonth}
                defaultMonth={defaultMonth}
                fixedWeeks
                components={{
                    MonthCaption: monthCaptionComponent,
                }}
                classNames={DAY_PICKER_CLASS_NAMES}
                formatters={formatters}
                labels={labels}
            />
        </div>
    );
};

export default Calendar;
